import React, { useState } from 'react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { masterScenarios } from '../services/masterApi';
import LoadingSpinner from '../components/LoadingSpinner';

export default function MasterScenarios() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const { data: scenariosData, isLoading } = useQuery(
    'masterScenarios',
    () => masterScenarios.getAll(),
    { retry: 1 }
  );

  const scenarios = scenariosData?.data?.scenarios || [];

  const filteredScenarios = scenarios.filter((scenario) =>
    scenario.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    scenario.scenarioId.toString().includes(searchTerm)
  );

  const handleViewDetails = (scenario) => {
    setSelectedScenario(scenario);
    setShowDetailModal(true);
  };

  const handleCreateInvoice = (scenario) => {
    toast.success(`Creating invoice for scenario: ${scenario.name}`);
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900">FBR Scenarios</h1>
          <p className="text-gray-600 mt-2">Select a scenario to create invoices</p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search scenarios by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none"
          />
        </div>

        {/* Scenarios Grid */}
        {isLoading ? (
          <LoadingSpinner />
        ) : filteredScenarios.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredScenarios.map((scenario) => (
              <ScenarioCard
                key={scenario.scenarioId}
                scenario={scenario}
                onViewDetails={handleViewDetails}
                onCreateInvoice={handleCreateInvoice}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500 text-lg">
              {searchTerm ? 'No scenarios found matching your search.' : 'No scenarios available.'}
            </p>
          </div>
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedScenario && (
          <ScenarioDetailModal
            scenario={selectedScenario}
            onClose={() => setShowDetailModal(false)}
            onCreateInvoice={() => {
              handleCreateInvoice(selectedScenario);
              setShowDetailModal(false);
            }}
          />
        )}
      </div>
    </div>
  );
}

function ScenarioCard({ scenario, onViewDetails, onCreateInvoice }) {
  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold mb-2">
            SN{scenario.scenarioId}
          </div>
          <h3 className="text-xl font-bold text-gray-900">{scenario.name}</h3>
        </div>
      </div>

      <p className="text-gray-600 text-sm mb-4">{scenario.description}</p>

      {scenario.applicableTaxRates && (
        <div className="mb-4">
          <p className="text-sm font-semibold text-gray-700 mb-2">Tax Rates:</p>
          <div className="flex flex-wrap gap-2">
            {scenario.applicableTaxRates.map((rate, idx) => (
              <span key={idx} className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                {rate}%
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-3 pt-4 border-t border-gray-200">
        <button
          onClick={() => onViewDetails(scenario)}
          className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-2 px-4 rounded transition-colors"
        >
          View Details
        </button>
        <button
          onClick={() => onCreateInvoice(scenario)}
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded transition-colors"
        >
          Create Invoice
        </button>
      </div>
    </div>
  );
}

function ScenarioDetailModal({ scenario, onClose, onCreateInvoice }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">Scenario Details</h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-800 rounded-lg p-2 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Scenario Info */}
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">#</span>
              <div>
                <p className="text-sm text-gray-600">Scenario ID</p>
                <p className="text-xl font-bold text-gray-900">SN{scenario.scenarioId}</p>
              </div>
            </div>
          </div>

          {/* Name & Description */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">{scenario.name}</h3>
            <p className="text-gray-600">{scenario.description}</p>
          </div>

          {/* Tax Rates */}
          {scenario.applicableTaxRates && scenario.applicableTaxRates.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Applicable Tax Rates</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {scenario.applicableTaxRates.map((rate, idx) => (
                  <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-700">{rate}%</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fields Required */}
          {scenario.fieldsRequired && scenario.fieldsRequired.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Required Fields</h4>
              <ul className="grid grid-cols-2 gap-2">
                {scenario.fieldsRequired.map((field, idx) => (
                  <li key={idx} className="flex items-center text-gray-700">
                    <span className="text-green-600 mr-2">✓</span> {field}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Additional Info */}
          {scenario.notes && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900"><strong>Note:</strong> {scenario.notes}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex gap-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded transition-colors"
          >
            Close
          </button>
          <button
            onClick={onCreateInvoice}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded transition-colors"
          >
            Create Invoice for This Scenario
          </button>
        </div>
      </div>
    </div>
  );
}
